import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  formatNumber,
  FormSection,
  Input,
  MoneyText,
  SettingsGroup,
  Switch,
  Textarea,
} from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useEffect } from 'react';
import { History, MapPin, Settings2, Warehouse } from 'lucide-react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { AreaFields } from '../geography/AreaFields.tsx';
import { governorateOfDelegation } from '../geography/area-choices.ts';
import { useGeoAreas, type Geography } from '../geography/useGeoAreas.ts';
import type { DepotStock } from '../dashboard/stock.ts';
import { FormSheet } from '../FormSheet.tsx';
import { usePageContext } from '../usePageContext.ts';
import {
  DEPOT_LIMITS,
  depotFormOf,
  depotFormSchema,
  depotBodyOf,
  type DepotBody,
  type DepotFormValues,
} from './depots.ts';
import type { Location } from './inventory.ts';
import styles from './DepotSheet.module.css';

export type DepotDetails = {
  readonly figures: DepotStock | undefined;
  readonly currency: string | undefined;
  readonly onShowMovements: () => void;
};

export type DepotSheetProps = {
  api: TypedClient;
  depot: Location | null;
  details: DepotDetails | null;
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (body: DepotBody) => void;
};

type DepotFiguresProps = DepotDetails & { depot: Location };

const DepotFigures = ({ depot, figures, currency, onShowMovements }: DepotFiguresProps) => {
  const { t, locale } = usePageContext();
  const count = (value: number | undefined) => formatNumber(value ?? 0, locale);
  return (
    <section className={styles['figures']} aria-label={t('depots.figures', { name: depot.name })}>
      <dl>
        <div>
          <dt>{t('inventory.unitsAvailable')}</dt>
          <dd>{count(figures?.available)}</dd>
        </div>
        <div>
          <dt>{t('inventory.depotLow')}</dt>
          <dd>{count(figures?.low)}</dd>
        </div>
        <div>
          <dt>{t('depots.saleValue')}</dt>
          <dd>
            {currency === undefined ? (
              '—'
            ) : (
              <MoneyText money={{ amount: figures?.saleValue ?? '0', currency }} locale={locale} />
            )}
          </dd>
        </div>
      </dl>
      <Button
        variant="ghost"
        size="sm"
        icon={<History size={16} aria-hidden="true" />}
        onClick={onShowMovements}
      >
        {t('depots.showMovements')}
      </Button>
    </section>
  );
};

const useInferredGovernorate = (form: UseFormReturn<DepotFormValues>, geography: Geography | undefined) => {
  useEffect(() => {
    if (geography === undefined || form.getValues('governorate') !== '') return;
    const governorate = governorateOfDelegation(geography, 'name', form.getValues('city'));
    if (governorate !== '') form.setValue('governorate', governorate);
  }, [form, geography]);
};

export const DepotSheet = (props: DepotSheetProps) => {
  const { api, depot, details, submitting, errorMessage, onClose, onSubmit } = props;
  const { t } = usePageContext();
  const geography = useGeoAreas(api).data;
  const form = useForm<DepotFormValues>({
    resolver: zodResolver(depotFormSchema),
    defaultValues: depotFormOf(depot),
    mode: 'onBlur',
  });
  useInferredGovernorate(form, geography);
  const { errors } = form.formState;
  const tooLong = (failed: unknown) => (failed === undefined ? undefined : t('depots.tooLong'));
  const keepsDefault = depot?.isDefault === true;

  return (
    <FormSheet
      open
      formId="depot-form"
      title={depot?.name ?? t('depots.newTitle')}
      submitting={submitting}
      errorMessage={errorMessage}
      onClose={onClose}
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          onSubmit(depotBodyOf(values));
        })(event);
      }}
    >
      {depot === null || details === null ? null : <DepotFigures depot={depot} {...details} />}
      <FormSection
        variant="plain"
        title={t('depots.identitySection')}
        description={t('depots.identityHint')}
        icon={<Warehouse size={18} />}
      >
        <div className={styles['identity']}>
          <Input
            label={t('inventory.depotName')}
            required
            maxLength={DEPOT_LIMITS.name}
            errorText={errors.name ? t('depots.nameRequired') : undefined}
            {...form.register('name')}
          />
          <Input
            label={t('inventory.depotCode')}
            required
            dir="ltr"
            autoCapitalize="characters"
            maxLength={DEPOT_LIMITS.code}
            helperText={t('depots.codeHelp')}
            errorText={errors.code ? t('depots.codeRequired') : undefined}
            {...form.register('code')}
          />
        </div>
      </FormSection>
      <FormSection
        variant="plain"
        title={t('depots.placeSection')}
        description={t('depots.placeHint')}
        icon={<MapPin size={18} />}
      >
        <Input
          label={t('depots.phone')}
          type="tel"
          inputMode="tel"
          dir="ltr"
          maxLength={DEPOT_LIMITS.phone}
          errorText={tooLong(errors.phone)}
          {...form.register('phone')}
        />
        <Textarea
          label={t('depots.address')}
          rows={2}
          maxLength={DEPOT_LIMITS.address}
          errorText={tooLong(errors.address)}
          {...form.register('address')}
        />
        <AreaFields
          form={form}
          geography={geography}
          by="name"
          governorate="governorate"
          delegation="city"
          labels={{ governorate: t('orders.governorate'), delegation: t('depots.city') }}
        />
      </FormSection>
      <FormSection variant="plain" title={t('depots.settingsSection')} icon={<Settings2 size={18} />}>
        <SettingsGroup>
          <Switch
            label={t('depots.defaultSwitch')}
            helperText={keepsDefault ? t('depots.defaultKept') : t('depots.defaultHelp')}
            disabled={keepsDefault}
            {...form.register('isDefault')}
          />
        </SettingsGroup>
      </FormSection>
    </FormSheet>
  );
};
